import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Sparkles, Trash2, CheckCircle2, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { useAIConfig } from '@/hooks/useAIConfig';

const AIConfiguration = () => {
  const { configs, activeConfig, isLoading, createConfig, updateConfig, deleteConfig, testConnection } = useAIConfig();
  const [selectedProvider, setSelectedProvider] = useState<'anthropic' | 'openai' | 'local'>('anthropic');
  
  const [formData, setFormData] = useState({
    anthropic: { apiKey: '', modelName: 'claude-3-5-sonnet-20241022' },
    openai: { apiKey: '', modelName: 'gpt-4-turbo-preview' },
    local: { apiUrl: 'http://localhost:11434', modelName: 'llama2' }
  });

  const handleSubmit = async (provider: 'anthropic' | 'openai' | 'local') => {
    const data = formData[provider] as any;
    
    await createConfig.mutateAsync({
      provider,
      api_key: provider !== 'local' ? data.apiKey : undefined,
      api_url: provider === 'local' ? data.apiUrl : undefined,
      model_name: data.modelName,
      is_active: true
    });

    setFormData({
      ...formData,
      [provider]: provider === 'local' 
        ? { apiUrl: 'http://localhost:11434', modelName: 'llama2' }
        : { apiKey: '', modelName: data.modelName }
    });
  };

  const handleTest = async (provider: 'anthropic' | 'openai' | 'local') => {
    const data = formData[provider] as any;
    
    await testConnection.mutateAsync({
      provider,
      api_key: provider !== 'local' ? data.apiKey : undefined,
      api_url: provider === 'local' ? data.apiUrl : undefined,
      model_name: data.modelName
    });
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta configuração?')) {
      await deleteConfig.mutateAsync(id);
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    await updateConfig.mutateAsync({
      id,
      is_active: !currentActive
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-2">
        <Sparkles className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Configuração de IA para Geração de KB</h1>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Configure a IA que será usada para gerar automaticamente artigos da Base de Conhecimento a partir de solicitações resolvidas.
          Apenas uma configuração pode estar ativa por vez.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Nova Configuração</CardTitle>
          <CardDescription>Adicione uma nova configuração de IA</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedProvider} onValueChange={(v) => setSelectedProvider(v as any)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="anthropic">Anthropic (Claude)</TabsTrigger>
              <TabsTrigger value="openai">OpenAI (GPT)</TabsTrigger>
              <TabsTrigger value="local">Local (Ollama)</TabsTrigger>
            </TabsList>

            <TabsContent value="anthropic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="anthropic-key">API Key *</Label>
                <Input
                  id="anthropic-key"
                  type="password"
                  placeholder="sk-ant-..."
                  value={formData.anthropic.apiKey}
                  onChange={(e) => setFormData({
                    ...formData,
                    anthropic: { ...formData.anthropic, apiKey: e.target.value }
                  })}
                />
                <p className="text-sm text-muted-foreground">
                  Obtenha sua chave em: <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">console.anthropic.com</a>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="anthropic-model">Modelo</Label>
                <Input
                  id="anthropic-model"
                  placeholder="claude-3-5-sonnet-20241022"
                  value={formData.anthropic.modelName}
                  onChange={(e) => setFormData({
                    ...formData,
                    anthropic: { ...formData.anthropic, modelName: e.target.value }
                  })}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleTest('anthropic')}
                  variant="outline"
                  disabled={!formData.anthropic.apiKey || testConnection.isPending}
                >
                  {testConnection.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Testar Conexão
                </Button>
                <Button
                  onClick={() => handleSubmit('anthropic')}
                  disabled={!formData.anthropic.apiKey || createConfig.isPending}
                >
                  {createConfig.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Salvar Configuração
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="openai" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="openai-key">API Key *</Label>
                <Input
                  id="openai-key"
                  type="password"
                  placeholder="sk-..."
                  value={formData.openai.apiKey}
                  onChange={(e) => setFormData({
                    ...formData,
                    openai: { ...formData.openai, apiKey: e.target.value }
                  })}
                />
                <p className="text-sm text-muted-foreground">
                  Obtenha sua chave em: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">platform.openai.com/api-keys</a>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="openai-model">Modelo</Label>
                <Input
                  id="openai-model"
                  placeholder="gpt-4-turbo-preview"
                  value={formData.openai.modelName}
                  onChange={(e) => setFormData({
                    ...formData,
                    openai: { ...formData.openai, modelName: e.target.value }
                  })}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleTest('openai')}
                  variant="outline"
                  disabled={!formData.openai.apiKey || testConnection.isPending}
                >
                  {testConnection.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Testar Conexão
                </Button>
                <Button
                  onClick={() => handleSubmit('openai')}
                  disabled={!formData.openai.apiKey || createConfig.isPending}
                >
                  {createConfig.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Salvar Configuração
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="local" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="local-url">URL da API *</Label>
                <Input
                  id="local-url"
                  placeholder="http://localhost:11434"
                  value={formData.local.apiUrl}
                  onChange={(e) => setFormData({
                    ...formData,
                    local: { ...formData.local, apiUrl: e.target.value }
                  })}
                />
                <p className="text-sm text-muted-foreground">
                  URL do Ollama ou LM Studio rodando localmente
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="local-model">Modelo</Label>
                <Input
                  id="local-model"
                  placeholder="llama2"
                  value={formData.local.modelName}
                  onChange={(e) => setFormData({
                    ...formData,
                    local: { ...formData.local, modelName: e.target.value }
                  })}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleTest('local')}
                  variant="outline"
                  disabled={!formData.local.apiUrl || testConnection.isPending}
                >
                  {testConnection.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Testar Conexão
                </Button>
                <Button
                  onClick={() => handleSubmit('local')}
                  disabled={!formData.local.apiUrl || createConfig.isPending}
                >
                  {createConfig.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Salvar Configuração
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configurações Salvas</CardTitle>
          <CardDescription>Gerencie suas configurações de IA</CardDescription>
        </CardHeader>
        <CardContent>
          {configs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma configuração salva ainda
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map((config) => (
                <div
                  key={config.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{config.provider}</span>
                        {config.is_active && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Ativa
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Modelo: {config.model_name || 'Padrão'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Criada em: {new Date(config.created_at).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(config.id, config.is_active)}
                      disabled={updateConfig.isPending}
                    >
                      {config.is_active ? (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          Desativar
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Ativar
                        </>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(config.id)}
                      disabled={deleteConfig.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {activeConfig && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Configuração ativa: <strong className="capitalize">{activeConfig.provider}</strong> ({activeConfig.model_name || 'Modelo padrão'})
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default AIConfiguration;
